import { ReactionCount } from "@/api/conversations";
import { api } from './client';

export interface ApiClubListItem {
    id: number;
    title: string;
    avatar_url: string | null;
    members_count: number;
    is_member: boolean;
    is_muted: boolean;
    requires_subscription?: boolean;
    unread_count?: number;
}

export interface ApiClubMemberPreview {
    id: string;
    name: string;
    age: number;
    avatar_id: string | null;
    is_online: boolean;
}

export interface ApiClubDetail {
    id: number;
    name: string;
    description: string;
    cover_id: string | null;
    stats: {
        members_count: number;
        messages_count: number;
        online_count: number;
    };
    active_members_preview: ApiClubMemberPreview[];
    is_member: boolean;
    is_muted: boolean;
}

export interface ApiReactionCount {
    reaction: string;
    count: number;
}

export interface ApiClubMessageSender {
    id: string;
    name: string;
    avatar_id: string | null;
}

export interface ApiClubMessage {
    id: number;
    club_id: number;
    created_at: string;
    message: string;
    type: 'text' | 'media' | 'system';
    is_read: boolean;
    file_url: string | null;
    file_extension: string | null;
    is_image: boolean;
    is_video: boolean;
    is_document: boolean;
    reactions: ApiReactionCount[];
    my_reaction: string | null;
    reply_to?: ApiClubMessageReply | null;
    reply_to_message_id?: number | null;
    is_deleted?: boolean;
    deleted_at?: string | null;
    is_edited?: boolean;
    edited_at?: string | null;
    sender: ApiClubMessageSender;
}

export interface ApiClubMessageReply {
    id: number;
    sender_id?: string;
    sender_name?: string;
    type?: 'text' | 'media' | 'system';
    message?: string | null;
    file_url?: string | null;
    is_deleted?: boolean;
    created_at?: string;
}

export interface ApiClubMessagesResponse {
    club: {
        id: number;
        title: string;
        cover_id: string | null;
        members_count: number;
    };
    next_cursor: number | null;
    messages: ApiClubMessage[];
}

export interface ApiClubMember {
    id: string;
    name: string;
    age: number;
    avatar_id: string | null;
    joined_at: string;
}

export interface ApiClubMembersResponse {
    next_cursor: number | null;
    members: ApiClubMember[];
}

export interface ApiToggleReactionResponse {
    toggled: 'set' | 'unset';
    reaction: string;
    counts: ApiReactionCount[];
}

export interface ClubMessage {
    id?: number;
    club_id: number;

    created_at: string;
    sender_id: string;
    sender_name?: string;
    sender_avatar?: string;

    message: string;
    type: 'text' | 'media' | 'system';

    is_read: boolean;

    file_url?: string;
    file_extension?: string;
    is_image?: boolean;
    is_video?: boolean;
    is_document?: boolean;

    reactions?: ReactionCount[];
    my_reaction?: string;

    reply_to?: ApiClubMessageReply | null;
    reply_to_message_id?: number | null;
    is_deleted?: boolean;
    deleted_at?: string | null;
    is_edited?: boolean;
    edited_at?: string | null;
}

export async function fetchClubs(): Promise<ApiClubListItem[]> {
    const { data } = await api.get<{ meta: { status: number }; data: ApiClubListItem[] }>('/clubs');
    return data.data;
}

export async function fetchClubById(clubId: number): Promise<ApiClubDetail> {
    const { data } = await api.get<{ meta: { error: string | null; status: number }; data: ApiClubDetail }>(`/clubs/${clubId}`);
    return data.data;
}

export async function joinClub(clubId: number): Promise<{ club_id: number; status: 'joined' }> {
    const { data } = await api.post<{ meta: { status: number }; data: { club_id: number; status: 'joined' } }>('/clubs/join', { club_id: clubId });
    return data.data;
}

export async function leaveClub(clubId: number): Promise<{ club_id: number; status: 'left' }> {
    const { data } = await api.post<{ meta: { status: number }; data: { club_id: number; status: 'left' } }>('/clubs/leave', { club_id: clubId });
    return data.data;
}

export async function muteClub(clubId: number): Promise<boolean> {
    const { data } = await api.post<{ meta: { status: number }; data: { muted: boolean } }>('/clubs/mute', { club_id: clubId });
    return data.data.muted;
}

export async function unmuteClub(clubId: number): Promise<boolean> {
    const { data } = await api.post<{ meta: { status: number }; data: { muted: boolean } }>('/clubs/unmute', { club_id: clubId });
    return data.data.muted;
}

export async function fetchClubMessages(
    clubId: number,
    params?: { cursor?: number; limit?: number }
): Promise<ApiClubMessagesResponse> {
    const { data } = await api.get<{ meta: { error: string | null; status: number }; data: ApiClubMessagesResponse }>(
        `/clubs/${clubId}/messages`,
        { params }
    );
    return data.data;
}

export async function sendClubMessage(
    clubId: number,
    payload: { message?: string; type?: string; file?: File; reply_to_message_id?: number | null }
): Promise<number> {
    console.log("[ClubAPI] sendClubMessage clubId:", clubId, "| type:", payload.type, "| message:", payload.message?.slice(0, 30));
    const form = new FormData();
    if (payload.message !== undefined) form.append('message', payload.message);
    if (payload.type !== undefined)    form.append('type', payload.type);
    if (payload.file)                  form.append('file', payload.file);
    if (payload.reply_to_message_id !== undefined && payload.reply_to_message_id !== null) {
        form.append('reply_to_message_id', String(payload.reply_to_message_id));
    }

    const { data } = await api.post<{ meta: { status: number }; data: { message_id: number } }>(
        `/clubs/${clubId}/messages`,
        form
    );
    console.log("[ClubAPI] sendClubMessage response:", data);
    return data.data.message_id;
}

export async function updateClubMessage(
    clubId: number,
    messageId: number,
    message: string
): Promise<Partial<ClubMessage>> {
    const { data } = await api.patch<{ meta: { status: number }; data: Partial<ClubMessage> }>(
        `/clubs/${clubId}/messages/${messageId}`,
        { message },
        { headers: { "Content-Type": "application/json; charset=utf-8" } }
    );
    return data.data;
}

export async function deleteClubMessage(
    clubId: number,
    messageId: number
): Promise<Partial<ClubMessage> | null> {
    const { data } = await api.delete<{ meta: { status: number }; data: Partial<ClubMessage> | null }>(
        `/clubs/${clubId}/messages/${messageId}`,
        { headers: { "Content-Type": "application/json; charset=utf-8" } }
    );
    return data.data ?? null;
}

export async function toggleClubReaction(
    clubId: number,
    messageId: number,
    reaction: string
): Promise<ApiToggleReactionResponse> {
    const { data } = await api.post<{ meta: { status: number }; data: ApiToggleReactionResponse }>(
        `/clubs/${clubId}/messages/${messageId}/reaction`,
        { reaction }
    );
    return data.data;
}

export async function fetchClubMembers(
    clubId: number,
    params?: { cursor?: number; limit?: number }
): Promise<ApiClubMembersResponse> {
    const { data } = await api.get<{ meta: { error: string | null; status: number }; data: ApiClubMembersResponse }>(
        `/clubs/${clubId}/members`,
        { params }
    );
    return data.data;
}
